// src/components/SidebarLayout.jsx
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import AuthModal from '../auth/AuthModal';
import { useAuth } from '../auth/AuthContext';
import { trackEvent } from '../lib/trackEvent';
import flowQuantLogo from '../assets/flowquantLogo.png';

const SidebarLayout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  const ADMIN_EMAIL = 'lawilkins13@gmail.com';
  const currentEmail = (user?.email || '').toLowerCase();
  const isAdmin = currentEmail === ADMIN_EMAIL;

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('fq_sidebar_collapsed');

    if (saved !== null) {
      setIsCollapsed(saved === 'true');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      'fq_sidebar_collapsed',
      String(isCollapsed)
    );
  }, [isCollapsed]);

  useEffect(() => {
    const onKey = (e) => {
      const key = (
        e && e.key ? String(e.key) : ''
      ).toLowerCase();

      if (
        key === 'b' &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault?.();

        setIsCollapsed((v) => !v);
      }
    };

    window.addEventListener('keydown', onKey, {
      passive: true,
    });

    return () =>
      window.removeEventListener('keydown', onKey);
  }, []);

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = mobileOpen
      ? 'hidden'
      : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const [showAuth, setShowAuth] = useState(false);

  const mainNavItems = [
    {
      name: 'Welcome',
      path: '/',
    },
    {
      name: 'Overview',
      path: '/overview',
    },
    {
      name: 'Journal',
      path: '/journal',
    },
    {
      name: 'Risk Calculator',
      path: '/risk-calculator',
    },
    {
      name: 'Updates & Feedback',
      path: '/updates',
    },
  ];

  const comingSoonItems = [
    {
      name: 'AI Insights',
      path: '/ai-insights',
    },
    {
      name: 'Broker Integrations',
      path: '/integrations',
    },
    {
      name: 'Advanced Analytics',
      path: '/advanced-analytics',
    },
    {
      name: 'Strategy Lab',
      path: '/strategy-lab',
    },
  ];

  const isActive = (path) =>
    location.pathname === path ||
    location.pathname.startsWith(path + '/');

  const SidebarInner = ({ compact = false }) => (
    <div className="flex flex-col h-full">

      {/* HEADER */}

      <div className="flex items-center justify-between h-14 px-3 border-b border-[#2a2a2a]">
        <Link
          to="/"
          onClick={() => setMobileOpen(false)}
          className="flex items-center gap-2 overflow-hidden min-w-0"
          title="Go to Welcome"
        >
          <img
            src={flowQuantLogo}
            alt="FlowQuant"
            className="w-8 h-8 shrink-0 rounded-lg object-cover"
          />

          {!compact && (
            <span className="truncate text-lg font-semibold">
              <span className="text-blue-400">
                Flow
              </span>

              <span className="text-white">
                Quant
              </span>
            </span>
          )}
        </Link>

        <button
          onClick={() =>
            setIsCollapsed((v) => !v)
          }
          className="inline-flex text-gray-400 hover:text-white p-1 rounded"
          title="Toggle sidebar"
        >
          {compact ? (
            <FaChevronRight />
          ) : (
            <FaChevronLeft />
          )}
        </button>
      </div>

      {/* Welcome banner */}

      {!compact && user && (
        <div className="px-3 py-2 border-b border-[#2a2a2a] text-sm text-gray-300">
          {user.first_name && user.last_name ? (
            <>
              Welcome,{' '}
              <span className="font-semibold">
                {user.first_name}{' '}
                {user.last_name}
              </span>
              !
            </>
          ) : (
            'Welcome, Guest!'
          )}
        </div>
      )}

      {/* Main nav */}

      <nav className="flex flex-col gap-1 p-3">
        {mainNavItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => {
              trackEvent(
                `clicked_${item.name
                  .toLowerCase()
                  .replace(/\s+/g, '_')}`,
                item.name
                  .toLowerCase()
                  .replace(/\s+/g, '_')
              );

              setMobileOpen(false);
            }}
            title={
              compact
                ? item.name
                : undefined
            }
            className={[
              'flex items-center rounded px-3 py-2 transition-colors text-sm',
              isActive(item.path)
                ? 'bg-[#0f0f0f] text-blue-400 border border-[#2a2a2a]'
                : 'text-gray-200 hover:bg-[#202020]',
            ].join(' ')}
          >
            {compact ? (
              <span className="w-5 text-center">
                {item.name.charAt(0)}
              </span>
            ) : (
              <span>{item.name}</span>
            )}
          </Link>
        ))}
      </nav>

      {/* Coming Soon Section */}

      <div className="px-3 mt-6">
        {!compact && (
          <span className="text-xs uppercase tracking-wide text-gray-400 block mb-2">
            Coming Soon
          </span>
        )}

        <nav className="flex flex-col space-y-2">
          {comingSoonItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => {
                trackEvent(
                  `clicked_${item.name
                    .toLowerCase()
                    .replace(/\s+/g, '_')}`,
                  item.name
                    .toLowerCase()
                    .replace(/\s+/g, '_')
                );

                setMobileOpen(false);
              }}
              title={
                compact
                  ? item.name
                  : undefined
              }
              className={[
                'flex items-baseline justify-between rounded px-3 py-2 text-sm transition-colors',
                isActive(item.path)
                  ? 'bg-[#0f0f0f] text-blue-400 border border-[#2a2a2a]'
                  : 'text-gray-300 hover:text-gray-100 hover:bg-[#202020]',
              ].join(' ')}
            >
              {compact ? (
                <span className="w-5 text-center">
                  {item.name.charAt(0)}
                </span>
              ) : (
                <span>{item.name}</span>
              )}

              {!compact && (
                <span className="text-[11px] text-gray-400 ml-2 whitespace-nowrap">
                  Coming&nbsp;Soon
                </span>
              )}
            </Link>
          ))}
        </nav>
      </div>

      {/* Administration Section */}

      {isAdmin && (
        <div className="px-3 mt-6">
          {!compact && (
            <span className="text-xs uppercase tracking-wide text-gray-400 block mb-2">
              Administration
            </span>
          )}

          <nav className="flex flex-col gap-1">
            <Link
              to="/users-admin"
              onClick={() => {
                trackEvent(
                  'clicked_user_admin',
                  'user_admin'
                );

                setMobileOpen(false);
              }}
              title={
                compact
                  ? 'User Admin'
                  : undefined
              }
              className={[
                'flex items-center rounded px-3 py-2 transition-colors text-sm',
                isActive('/users-admin')
                  ? 'bg-[#0f0f0f] text-blue-400 border border-[#2a2a2a]'
                  : 'text-gray-200 hover:bg-[#202020]',
              ].join(' ')}
            >
              {compact ? (
                <span className="w-5 text-center">
                  A
                </span>
              ) : (
                <span>User Admin</span>
              )}
            </Link>
          </nav>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#121212] text-white">

      {/* Mobile top bar */}

      <div className="md:hidden fixed inset-x-0 top-0 h-14 z-30 bg-[#181818] border-b border-[#2a2a2a] flex items-center justify-between px-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() =>
              setMobileOpen(true)
            }
            className="p-2 rounded bg-[#222] border border-[#2a2a2a]"
          >
            ☰
          </button>

          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-semibold truncate"
            title="Go to Welcome"
          >
            <img
              src={flowQuantLogo}
              alt="FlowQuant"
              className="w-7 h-7 shrink-0 rounded-lg object-cover"
            />

            <span className="truncate">
              <span className="text-blue-400">
                Flow
              </span>

              <span className="text-white">
                Quant
              </span>
            </span>
          </Link>
        </div>

        <button
          onClick={() => {
            if (user) {
              setMobileOpen(false);
              logout();
            } else {
              setShowAuth(true);
            }
          }}
          className={[
            'text-xs px-2.5 py-1.5 rounded-md transition-colors',
            user
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-blue-600 hover:bg-blue-700',
          ].join(' ')}
        >
          {user
            ? 'Logout'
            : 'Login / Register'}
        </button>
      </div>

      {/* Desktop floating auth button */}

      <div className="hidden md:flex fixed top-4 right-4 z-30">
        <button
          onClick={() => {
            if (user) {
              logout();
            } else {
              setShowAuth(true);
            }
          }}
          className={[
            'text-xs px-3 py-1.5 rounded-md shadow-md transition-colors border border-[#2a2a2a] opacity-90',
            user
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white',
          ].join(' ')}
        >
          {user
            ? 'Logout'
            : 'Login / Register'}
        </button>
      </div>

      {/* Desktop sidebar */}

      <aside
        className={`hidden md:flex transition-all duration-300 bg-[#181818] border-r border-[#2a2a2a] ${
          isCollapsed
            ? 'w-16'
            : 'w-64'
        }`}
      >
        <SidebarInner
          compact={isCollapsed}
        />
      </aside>

      {/* Mobile Drawer */}

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40"
            onClick={() =>
              setMobileOpen(false)
            }
          />

          <div className="fixed inset-y-0 left-0 w-64 z-50 bg-[#181818] border-r border-[#2a2a2a] shadow-xl animate-[slideIn_.2s_ease-out]">
            <SidebarInner
              compact={false}
            />
          </div>
        </>
      )}

      {/* Main content */}

      <main className="flex-1 p-6 w-full md:ml-0 pt-16 md:pt-6">
        {children}
      </main>

      {/* Auth modal */}

      <AuthModal
        isOpen={showAuth}
        onClose={() => {
          setShowAuth(false);
          setMobileOpen(false);
        }}
      />

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(-100%);
            opacity: .5;
          }

          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default SidebarLayout;